import type { WalletDebitsTranslations } from "../interfaces/walletDebits";

export const walletDebitsTranslations: WalletDebitsTranslations = {
  page_title: "Wallet debits",
  page_subtitle:
    "Take a client's balance for a send Coloca makes by hand, outside the platform. The amount is frozen until you record the send with its reference; every debit asks for your verification code and is booked in the ledger.",
  action_refresh: "Refresh the history",
  action_new: "New debit",
  action_retry: "Retry",
  filter_organization: "Organization",
  filter_all_option: "All organizations",
  filter_all_hint: "Showing the debits of every organization.",
  filter_org_hint: "Showing only this organization's debits.",

  empty_title: "No debits yet",
  empty_description: "The debits you record show up here with the send's reference and its supporting files.",
  col_date: "Date",
  col_organization: "Organization",
  col_amount: "Amount",
  col_concept: "Concept and internal note",
  col_external_reference: "Send reference",
  col_reference: "Reference",
  col_by: "Made by",
  col_attachments: "Supporting files",
  card_note: "Internal note: {{note}}",
  card_by: "Made by {{email}}",
  card_external_reference: "Send: {{reference}}",
  attachments_count: "{{count}} files",
  attachments_count_one: "1 file",
  attachments_count_none: "No files",

  dialog_title: "Debit a wallet",
  dialog_title_review: "Review the debit",
  dialog_title_done: "Balance taken",
  dialog_title_processing: "Taking the balance…",
  dialog_description:
    "Take the amount out of the client's available balance for a manual send. It stays frozen until you record the send with its reference, or cancel the debit.",
  dialog_description_review:
    "Check the organization, the wallet, the amount and how it is sent before you authorize.",
  dialog_description_done:
    "The amount is frozen in the client's wallet. Make the send and record its reference here.",
  dialog_description_processing:
    "We are taking the balance and booking it in the ledger; it can take a few seconds. Do not close this window.",
  processing_to: "Manual send",
  processing_transit_aria: "{{amount}} {{currency}} leave {{name}}'s {{currency}} wallet for a manual send.",
  action_cancel: "Cancel",
  action_review: "Review debit",
  action_back: "Back",
  action_confirm: "Authorize debit",
  action_another: "Make another debit",
  action_close: "Close",

  field_organization: "Organization",
  field_organization_required_hint: "Choose the organization the send was made for.",
  field_organization_hint: "The debit comes out of one of this organization's wallets.",
  organization_unknown: "the organization",
  field_wallet: "Wallet",
  field_wallet_hint:
    "The wallet's currency is the debit's currency. Only the available balance can be debited.",
  field_wallet_placeholder: "Choose a wallet",
  field_wallet_none: "This organization has no activated wallets, so there is no balance to debit.",
  field_wallet_option: "{{currency}} · available {{balance}}",
  field_wallet_option_empty: "{{currency}} · no available balance",
  field_wallet_option_inactive: "{{currency}} · {{status}}",
  wallet_frozen: "frozen",
  wallet_closed: "closed",
  field_amount: "Amount",
  field_amount_hint: "In {{currency}}. Available: {{available}}.",
  field_corridor: "How it was sent",
  corridor_other: "Another way",
  corridor_other_hint: "No fee: the full amount is sent.",
  send_preview_send: "send",
  send_preview_fees: "Fee {{commission}} · taxes {{taxes}}",
  send_preview_hint:
    "The client pays the fee and taxes of a normal send on that rail, at its organization's rates. The debit takes the full amount and what is sent is the rest.",
  send_preview_needs_amount: "Enter the amount to see how much to send on each rail.",
  send_preview_below_fees: "the fees exceed the amount",
  send_preview_no_fee_configured: "no fees configured",
  send_preview_no_corridors: "No send rails are configured for {{currency}}.",
  send_preview_failed: "Could not work out how much to send: {{message}}",
  validation_corridor: "Choose how the send was made.",
  validation_corridor_unpriced: "Wait for that rail's fee to be worked out, or choose another way.",
  review_corridor: "How it was sent",
  review_commission: "Fee",
  review_taxes: "Taxes",
  review_send_by_hand: "Sent by hand",
  done_fees: "What the client paid",
  field_external_reference: "Send reference",
  field_external_reference_placeholder: "E.g. TRF-20261027-004512",
  field_concept: "Concept",
  field_concept_hint: "The client sees it in their statement.",
  concept_default: "Manual send",
  field_note: "Internal note",
  field_note_hint: "Only the Coloca team sees it: why the send was made by hand and who asked for it.",
  field_note_placeholder: "E.g. rail down, requested by support #519",
  field_attachments: "Supporting files",

  validation_wallet: "Choose the wallet to debit.",
  validation_amount: "Enter an amount greater than zero.",
  validation_amount_precision: "{{currency}} takes at most {{digits}} decimals.",
  validation_amount_exceeds: "More than the available balance ({{available}}).",
  validation_concept: "Enter the concept the client will see.",
  validation_note: "Enter an internal note for the record.",

  review_organization: "Organization",
  review_wallet: "Wallet",
  review_wallet_value: "{{currency}} wallet",
  review_balance_before: "Available balance now",
  review_amount: "Debit for the send",
  review_balance_after: "Balance after the debit",
  review_concept: "Concept (the client sees it)",
  review_note: "Internal note",
  review_client_sees:
    "The client will see it in their statement as a debit from Coloca Payments with the concept. They get no email; you and accounting get the notice when you record the send.",
  review_checkbox: "I checked the organization, the wallet, the amount and how it is sent",

  two_factor_required:
    "To debit wallets you need two-step verification turned on. Turn it on in Settings and come back.",
  two_factor_description: "You are about to debit {{amount}} {{currency}} from {{name}}'s wallet.",

  unresolved_title: "A debit was left unconfirmed",
  unresolved_text:
    "A debit of {{amount}} ({{reference}}) to this organization was authorized and its answer never arrived, so we do not know whether it was taken. Check it before making another.",
  unresolved_text_retry:
    "No answer came back, so we do not know whether this debit went through. Check it, or authorize it again: if it was already made, it is not debited twice.",
  unresolved_action: "Check the debit",
  unresolved_pending: "It is still being processed. Check again in a few seconds.",
  unresolved_unknown: "We could not check it. Try again in a moment.",
  unresolved_not_placed: "The unconfirmed debit did not go through. You can make the debit now.",
  debit_busy:
    "Another tab is sending a debit to this organization. Wait for it to finish and authorize again; nothing was sent.",
  unresolved_blocked: "A debit to this organization is unconfirmed. Check it before making another.",

  done_recovered:
    "The unconfirmed debit did go through. This is its record; checking it debited nothing again.",
  done_replayed:
    "This debit had already been made with the same details. Nothing was debited again and no emails were sent.",
  done_receipt_label: "Debit",
  done_amount: "Amount taken",
  done_organization: "Organization",
  done_wallet: "Wallet",
  done_concept: "Concept",
  done_external_reference: "Send reference",
  done_emails_label: "Notices sent",
  email_pending: "Sending the notice {{who}}.",
  email_sent: "We sent the notice {{who}}.",
  email_failed: "We could not send the notice {{who}}. The debit was recorded anyway.",
  email_to_operator: "to your email",
  email_to_accounting: "to accounting's email",

  attachments_title: "Supporting files",
  attachments_empty: "This debit has no supporting files yet.",
  attachments_uploading: "Uploading the supporting files…",
  attachments_uploaded: "Supporting files uploaded.",
  attachments_failed:
    "We could not upload the supporting files: {{message}} The debit was recorded; try again.",
  attachments_retry: "Upload again",
  attachments_upload: "Upload files",
  attachments_open: "Open {{name}}",
  attachments_unavailable: "Link unavailable",
  attachments_full: "This debit already has the maximum of {{max}} supporting files.",
  attachments_meta: "{{size}} · {{email}}",
  col_status: "Status",
  status_reserved: "Waiting for the send",
  status_reserved_stale: "Send pending for over a day",
  status_sent: "Sent",
  status_cancelled: "Cancelled",
  member_status: "Status",
  member_status_reserved: "In progress",
  member_status_sent: "Completed",
  member_status_cancelled: "Cancelled: the balance went back to your wallet",
  review_held:
    "The amount stays frozen in the client's wallet until you record the send or cancel the debit. Once sent it cannot be cancelled: it is corrected with a top-up.",
  review_to_send_by_hand: "To send by hand",
  done_sent_by: "Send recorded by",
  pending_heading: "Send {{amount}} and record the send",
  pending_text_rail:
    "Send it over {{rail}} from the bank or the provider. The amount is already frozen in the client's wallet: they cannot use it meanwhile.",
  pending_text_other:
    "Send it the agreed way. The amount is already frozen in the client's wallet: they cannot use it meanwhile.",
  pending_section_title: "Waiting for the send",
  pending_section_hint:
    "Frozen balance whose send is not recorded yet, oldest first. Open one to record its send or cancel it.",
  pending_section_failed: "We could not load the pending debits: {{message}}",
  send_title: "Record the send",
  send_hint:
    "Once you made the send, enter its reference at the bank or the provider. You can attach the receipt now or later.",
  send_action: "Record send",
  send_upload_failed:
    "The send is recorded, but the supporting files did not upload: {{message}} You can upload them below.",
  cancel_open: "The send did not happen: cancel the debit",
  cancel_title: "Cancel the debit",
  cancel_hint:
    "The {{amount}} go back to the client's available balance and the debit is reversed in their statement.",
  cancel_reason: "Reason",
  cancel_reason_placeholder: "E.g. the bank rejected the transfer",
  cancel_confirm: "Cancel debit",
  cancel_back: "Back",
  cancel_two_factor: "You are about to cancel debit {{reference}} and give the balance back to the client.",
  cancelled_text:
    "Cancelled by {{who}} on {{when}}. Reason: {{reason}}. The balance went back to the client.",
  reference_edit: "Edit",
  reference_edit_hint: "The previous reference stays in the history.",
  reference_save: "Save reference",
  attachments_remove: "Remove",
  attachments_remove_aria: "Remove {{name}}",
  attachments_remove_confirm: "Remove this file? It stays stored and in the history.",
  attachments_remove_yes: "Yes, remove",
  timeline_title: "History",
  timeline_failed: "We could not load the history: {{message}}",
  timeline_reserved: "Balance taken",
  timeline_sent: "Send recorded",
  timeline_cancelled: "Debit cancelled",
  timeline_reference_changed: "Reference corrected",
  timeline_attachments_added: "Files added",
  timeline_attachment_removed: "File removed",
  timeline_reference_detail: "{{from}} → {{to}}",
  timeline_two_factor: "with 2FA",
};
